<script setup lang="ts">
  import { computed, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { Tags } from "lucide-vue-next";
  import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
  import { Button } from "@/components/ui/button";
  import { Select } from "@/components/episto-ui/select";
  import { useMAssetLibraryStore } from "@stores/mAssetLibrary";

  const assetLibraryStore = useMAssetLibraryStore();
  const { allAssetTags, selectedIds, selectedAssets } = storeToRefs(assetLibraryStore);
  const { bulkUpdateTags } = assetLibraryStore;

  const open = ref(false);
  const tagsToAdd = ref<string[]>([]);
  const tagsToRemove = ref<string[]>([]);
  const isSaving = ref(false);

  const addOptions = computed(() => allAssetTags.value.map((tag) => ({ value: tag.name, label: tag.name })));

  // Only the tags the selection actually carries can be removed from it.
  const removeOptions = computed(() => {
    const carried = new Set(selectedAssets.value.flatMap((asset) => asset.asset_tags ?? []));
    return [...carried].sort().map((name) => ({ value: name, label: name }));
  });

  const selectionLabel = computed(() => `${selectedIds.value.length} asset${selectedIds.value.length > 1 ? "s" : ""}`);
  const hasChanges = computed(() => tagsToAdd.value.length > 0 || tagsToRemove.value.length > 0);

  const reset = () => {
    tagsToAdd.value = [];
    tagsToRemove.value = [];
  };

  const apply = async () => {
    isSaving.value = true;
    try {
      const succeeded = await bulkUpdateTags([...selectedIds.value], { add: tagsToAdd.value, remove: tagsToRemove.value });
      if (!succeeded) return;
      reset();
      open.value = false;
    } finally {
      isSaving.value = false;
    }
  };
</script>

<template>
  <Dialog
    v-model:open="open"
    @update:open="(value) => !value && reset()">
    <Button
      variant="outline"
      size="sm"
      @click="open = true">
      <Tags class="mr-1 h-4 w-4" />
      Edit tags
    </Button>
    <DialogContent class="max-w-md">
      <DialogHeader>
        <DialogTitle>Edit tags of {{ selectionLabel }}</DialogTitle>
        <DialogDescription>Tags are added to or removed from every selected asset; their other tags stay as they are.</DialogDescription>
      </DialogHeader>

      <div class="space-y-4">
        <div class="space-y-1">
          <label class="text-xs font-medium text-muted-foreground">Add tags</label>
          <Select
            v-model="tagsToAdd"
            :options="addOptions"
            multiple
            filterable
            creatable
            placeholder="Tags to add" />
        </div>
        <div class="space-y-1">
          <label class="text-xs font-medium text-muted-foreground">Remove tags</label>
          <Select
            v-model="tagsToRemove"
            :options="removeOptions"
            multiple
            filterable
            placeholder="Tags to remove" />
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="ghost"
          :disabled="isSaving"
          @click="open = false">
          Cancel
        </Button>
        <Button
          :disabled="!hasChanges || isSaving"
          @click="apply">
          Apply to {{ selectionLabel }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
